import type { ReactNode } from 'react'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * One panel of the Reports workspace.
 *
 * The flow is the legacy screen's — pick from the tree, narrow with the
 * report's own filters, set the period, read the detail — but the surface is
 * this app's: cards on a tinted ground rather than the old blue title bars.
 *
 * Collapsing is load-bearing, not decoration. A chart is read at full width,
 * and all three narrow panels are reference material once a report is running.
 * A collapsed panel stays as a labelled rail so nothing disappears without an
 * obvious way back.
 */
export function ReportsPanel({
  title,
  width = 'w-56',
  collapsed,
  onToggle,
  children,
}: {
  title: string
  width?: string
  collapsed: boolean
  onToggle: () => void
  children: ReactNode
}) {
  if (collapsed) {
    return (
      <button
        type='button'
        onClick={onToggle}
        aria-label={`Expand ${title}`}
        aria-expanded={false}
        title={`Expand ${title}`}
        className='group flex w-10 shrink-0 flex-col items-center gap-3 rounded-xl border bg-primary py-3 text-primary-foreground shadow-sm transition-colors hover:bg-primary/90'
      >
        <PanelLeftOpen className='h-4 w-4 shrink-0' />
        <span className='select-none whitespace-nowrap text-xs font-medium tracking-wide [writing-mode:vertical-rl]'>
          {title}
        </span>
      </button>
    )
  }

  return (
    <section className={cn('flex shrink-0 flex-col rounded-xl border bg-background shadow-sm', width)}>
      <PanelHeader title={title} onCollapse={onToggle} />
      <div className='min-h-0 flex-1 overflow-y-auto p-2'>{children}</div>
    </section>
  )
}

/** The detail panel: same card, fills the rest, never collapses. */
export function ReportsDetailPanel({ children }: { children: ReactNode }) {
  return (
    <section className='flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-background shadow-sm'>
      {children}
    </section>
  )
}

function PanelHeader({ title, onCollapse }: { title: string; onCollapse: () => void }) {
  return (
    // The app's own primary blue, not a new one — the same token every primary
    // control wears, so the two report headers read as one surface.
    <div className='flex h-10 shrink-0 items-center gap-1.5 border-b bg-primary px-3 text-primary-foreground'>
      <h2 className='flex-1 truncate text-xs font-semibold uppercase tracking-wide'>
        {title}
      </h2>
      <button
        type='button'
        onClick={onCollapse}
        aria-label={`Collapse ${title}`}
        aria-expanded
        className='flex h-6 w-6 items-center justify-center rounded-md text-primary-foreground/80 transition-colors hover:bg-white/15 hover:text-primary-foreground'
      >
        <PanelLeftClose className='h-3.5 w-3.5' />
      </button>
    </div>
  )
}
